﻿
'use strict';

function runSoon(callback) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout: 900 });
    return;
  }

  window.setTimeout(callback, 1);
}

function normalizeProductGridWhitespace(root) {
  root.querySelectorAll('.product-grid').forEach((grid) => {
    [...grid.childNodes].forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) {
        node.remove();
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  normalizeProductGridWhitespace(document);
  initCartDrawer();

  if (document.querySelector('[data-product-form]')) {
    initProductForms(document);
  }

  if (document.querySelector('[data-card-form]')) {
    runSoon(() => initDeferredProductCardForms(document));
  }

  if (document.querySelector('[data-gallery]')) {
    initMediaGallery(document);
  }

  if (document.querySelector('.qty-picker:not([data-qty-mode="variant"])')) {
    initQuantityPickers(document);
  }

  if (document.querySelector('[data-site-header]')) {
    window.requestAnimationFrame(initStickyHeader);
  }

  if (document.querySelector('[data-hamburger]')) {
    runSoon(initMobileNav);
  }

  if (document.querySelector('.reveal')) {
    runSoon(initReveal);
  }

  if (document.querySelector('[data-sort-select]')) {
    runSoon(initSortSelect);
  }

  if (document.querySelector('a[href*="wa.me/"], a[href*="api.whatsapp.com/send"]')) {
    runSoon(() => normalizeWhatsAppLinks(document));
  }

  if (document.querySelector('[data-cart-page-summary]')) {
    runSoon(initCartPageCheckout);
  }

  runSoon(initPreventDoubleTapZoom);
  runSoon(openCartFromUrl);
});

function initPreventDoubleTapZoom() {
  const supportsTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
  if (!supportsTouch || !isCoarsePointer) return;

  let lastTouchEnd = 0;

  document.addEventListener('touchend', (event) => {
    if (event.touches.length > 0) return;

    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) {
      lastTouchEnd = Date.now();
      return;
    }

    const now = Date.now();
    if (now - lastTouchEnd <= 320) {
      event.preventDefault();
    }
    lastTouchEnd = now;
  }, { passive: false });
}

function initStickyHeader() {
  const header = document.querySelector('[data-site-header]');
  if (!header) return;
  const sync = () => header.classList.toggle('is-scrolled', window.scrollY > 16);
  sync();
  window.addEventListener('scroll', sync, { passive: true });
}

function initMobileNav() {
  const hamburger  = document.querySelector('[data-hamburger]');
  const mobileNav  = document.querySelector('[data-mobile-nav]');
  const overlay    = document.querySelector('[data-overlay]');
  if (!hamburger || !mobileNav) return;
  const firstLink = mobileNav.querySelector('a, button, [tabindex]:not([tabindex="-1"])');

  const open  = () => {
    hamburger.classList.add('is-open');
    mobileNav.classList.add('is-open');
    mobileNav.removeAttribute('inert');
    mobileNav.setAttribute('aria-hidden', 'false');
    overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    hamburger.setAttribute('aria-expanded', 'true');
    hamburger.setAttribute('aria-label', 'Fechar menu');
    window.requestAnimationFrame(() => firstLink?.focus());
  };
  const close = () => {
    hamburger.classList.remove('is-open');
    mobileNav.classList.remove('is-open');
    mobileNav.setAttribute('inert', '');
    mobileNav.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
    hamburger.setAttribute('aria-expanded', 'false');
    hamburger.setAttribute('aria-label', 'Abrir menu');
    if (mobileNav.contains(document.activeElement)) hamburger.focus();
  };

  hamburger.addEventListener('click', () =>
    hamburger.classList.contains('is-open') ? close() : open()
  );
  overlay?.addEventListener('click', close);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && hamburger.classList.contains('is-open')) close();
  });

  // Close nav on link click
  mobileNav.querySelectorAll('a').forEach(a => a.addEventListener('click', close));
}

function normalizeWhatsAppLinks(root) {
  const links = root.querySelectorAll('a[href*="wa.me/"], a[href*="api.whatsapp.com/send"]');
  if (!links.length) return;

  links.forEach((link) => {
    const rawHref = link.getAttribute('href');
    if (!rawHref) return;

    let parsed;
    try {
      parsed = new URL(rawHref, window.location.origin);
    } catch {
      return;
    }

    let phone = '';
    let text = parsed.searchParams.get('text') || '';

    if (parsed.hostname.includes('wa.me')) {
      phone = parsed.pathname.replace(/\//g, '').trim();
    } else if (parsed.hostname.includes('api.whatsapp.com')) {
      phone = parsed.searchParams.get('phone') || '';
    }

    phone = phone.replace(/\D/g, '');
    if (!phone) return;

    const normalized = new URL('https://api.whatsapp.com/send');
    normalized.searchParams.set('phone', phone);
    if (text) normalized.searchParams.set('text', text);

    link.setAttribute('href', normalized.toString());
  });
}

function initReveal() {
  const items = document.querySelectorAll('.reveal');
  if (!items.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    items.forEach(el => el.classList.add('is-visible'));
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.12 });
  items.forEach(el => observer.observe(el));
}

function initQuantityPickers(root) {
  root.querySelectorAll('.qty-picker').forEach((picker) => {
    if (picker.dataset.qtyMode === 'variant') return;
    if (picker.dataset.ready === 'true') return;
    picker.dataset.ready = 'true';

    const input   = picker.querySelector('[data-qty-input]');
    const display = picker.querySelector('[data-qty-display]');
    const btnMinus = picker.querySelector('[data-qty-minus]');
    const btnPlus  = picker.querySelector('[data-qty-plus]');
    if (!input) return;

    const getMin = () => parseInt(input.min || '1', 10);
    const getVal = () => Math.max(getMin(), parseInt(input.value || '1', 10));

    const updateDisplay = () => {
      const val = getVal();
      if (display) display.textContent = val;
      if (btnMinus) btnMinus.disabled = val <= getMin();
    };

    btnMinus?.addEventListener('click', () => {
      input.value = Math.max(getMin(), getVal() - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    btnPlus?.addEventListener('click', () => {
      input.value = getVal() + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    input.addEventListener('change', updateDisplay);
    updateDisplay();
  });
}

function initMediaGallery(root) {
  root.querySelectorAll('[data-gallery]').forEach((gallery) => {
    if (gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';

    const main   = gallery.querySelector('[data-gallery-main]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    if (!main || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src    = thumb.getAttribute('data-src');
        const srcset = thumb.getAttribute('data-srcset');
        const alt    = thumb.getAttribute('data-alt') || '';
        const img    = main.querySelector('img');
        if (!img || !src) return;

        img.src = src;
        if (srcset) img.srcset = srcset;
        img.alt = alt;

        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  });
}

function initProductForms(root) {
  root.querySelectorAll('[data-product-form]').forEach((form) => {
    if (form.dataset.ready === 'true') return;
    form.dataset.ready = 'true';

    const section       = form.closest('[data-product-section]');
    const variantsEl    = section?.querySelector('[data-product-variants]');
    if (!section || !variantsEl) return;

    let variants;
    try { variants = JSON.parse(variantsEl.textContent); } catch { return; }
    if (!Array.isArray(variants) || !variants.length) return;

    const variantInput        = form.querySelector('[data-variant-id]');
    const addBtn             = form.querySelector('[data-add-to-cart]');
    const priceWrap          = section.querySelector('[data-product-price]');
    const compareWrap        = section.querySelector('[data-product-compare]');
    const unitPriceEl        = section.querySelector('[data-product-unit-price]');
    const statusEl           = form.querySelector('[data-product-status]');
    const helperEl           = form.querySelector('[data-product-helper]');
    const qtyInput           = form.querySelector('[data-qty-input]');
    const optionInputs       = [...form.querySelectorAll('[data-option-input]')];
    const primaryOptionInputs = optionInputs.filter((input) => parseInt(input.getAttribute('data-option-position'), 10) === 1);
    const secondaryOptionInputs = optionInputs.filter((input) => parseInt(input.getAttribute('data-option-position'), 10) > 1);
    const variantDisplayEl   = form.querySelector('[data-product-variant-display]');
    const variantMinusBtn    = form.querySelector('[data-product-variant-minus]');
    const variantPlusBtn     = form.querySelector('[data-product-variant-plus]');
    const hasGroupedPrimarySelector = primaryOptionInputs.length > 0 && variantDisplayEl && variantMinusBtn && variantPlusBtn;

    const updateSelectedOptionLabel = (position, value) => {
      section.querySelectorAll(`[data-selected-option-${position}]`).forEach((el) => {
        el.textContent = value || '';
      });
    };

    const getSelectedOptionsByPosition = () => {
      const selected = [];
      optionInputs.forEach((input) => {
        if (!input.checked) return;
        const position = parseInt(input.getAttribute('data-option-position'), 10);
        if (!Number.isFinite(position) || position <= 0) return;
        selected[position - 1] = input.value;
      });
      return selected;
    };

    const syncPrimaryOptionValue = (value) => {
      if (!primaryOptionInputs.length || !value) return;
      const normalizedValue = normalizeLabel(value);
      primaryOptionInputs.forEach((input) => {
        input.checked = normalizeLabel(input.value) === normalizedValue;
      });
      updateSelectedOptionLabel(1, value);
    };

    const updateProductUI = ({
      label = '',
      variant = null,
      price = 0,
      compareAtPrice = 0,
      unitPriceText = '',
      helperText = '',
      quantity = 1,
      resetQuantity = true
    }) => {
      const nextQuantity = Math.max(1, parseInt(quantity, 10) || 1);
      if (variantInput) variantInput.value = variant?.id || '';
      if (qtyInput) qtyInput.value = String(resetQuantity ? 1 : nextQuantity);
      if (variantDisplayEl && label) variantDisplayEl.textContent = label;
      if (label) syncPrimaryOptionValue(label);

      if (priceWrap) priceWrap.textContent = formatMoney(price || 0);

      if (compareWrap) {
        const onSale = Number(compareAtPrice) > Number(price);
        compareWrap.hidden = !onSale;
        compareWrap.textContent = onSale ? formatMoney(compareAtPrice) : '';
      }

      if (unitPriceEl) {
        unitPriceEl.hidden = !unitPriceText;
        unitPriceEl.textContent = unitPriceText;
      }

      if (helperEl) {
        helperEl.hidden = !helperText;
        helperEl.textContent = helperText;
      }

      if (addBtn) {
        addBtn.classList.remove('is-loading', 'is-success');
        addBtn.disabled = !variant?.available;
        addBtn.textContent = variant?.available ? 'Adicionar ao carrinho' : 'Indisponível';
        addBtn._originalText = addBtn.textContent;
      }

      if (statusEl) {
        statusEl.textContent = variant?.available ? '' : 'Esta opção está esgotada.';
        statusEl.className = 'product-status' + (variant?.available ? '' : ' is-error');
      }
    };

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        const position = parseInt(input.getAttribute('data-option-position'), 10);
        if (!Number.isFinite(position) || position <= 0) return;
        updateSelectedOptionLabel(position, input.value);
      });
    });

    if (hasGroupedPrimarySelector) {
      let currentVariantGroups = [];
      let quantityExpansion = null;
      let selectedIndex = 0;
      let extraStepCount = 0;

      const findGroupIndex = (groups, label) => groups.findIndex((group) => normalizeLabel(group.label) === normalizeLabel(label));

      const syncAvailableGroups = (preferredLabel = '') => {
        const selectedOptions = getSelectedOptionsByPosition();
        const filteredVariants = variants.filter((variant) => (
          variant.options.every((optionValue, index) => {
            if (index === 0) return true;
            return !selectedOptions[index] || optionValue === selectedOptions[index];
          })
        ));

        currentVariantGroups = buildVariantGroups(filteredVariants);
        quantityExpansion = buildQuantityExpansionConfig(currentVariantGroups);

        if (!currentVariantGroups.length) {
          selectedIndex = 0;
          extraStepCount = 0;
          return;
        }

        let nextIndex = preferredLabel ? findGroupIndex(currentVariantGroups, preferredLabel) : -1;
        if (nextIndex < 0) {
          const checkedPrimaryValue = primaryOptionInputs.find((input) => input.checked)?.value;
          if (checkedPrimaryValue) nextIndex = findGroupIndex(currentVariantGroups, checkedPrimaryValue);
        }
        if (nextIndex < 0) nextIndex = currentVariantGroups.findIndex((group) => group.variant?.available);
        if (nextIndex < 0) nextIndex = 0;

        selectedIndex = nextIndex;
        extraStepCount = 0;
      };

      const getSelectionState = () => {
        const activeGroup = currentVariantGroups[selectedIndex] || currentVariantGroups[0];
        if (!activeGroup?.variant) return null;

        if (quantityExpansion && selectedIndex === currentVariantGroups.length - 1 && extraStepCount > 0) {
          const expandedSelection = buildExpandedSelectionState(
            quantityExpansion,
            extraStepCount,
            getVariantHelperText(currentVariantGroups)
          );
          if (expandedSelection) return expandedSelection;
        }

        return {
          label: activeGroup.label,
          variant: activeGroup.variant,
          price: activeGroup.variant.price,
          compareAtPrice: activeGroup.variant.compare_at_price,
          unitPriceText: getVariantUnitPriceText(activeGroup.variant, activeGroup.label),
          helperText: getVariantHelperText(currentVariantGroups),
          cartItems: [
            { id: activeGroup.variant.id, quantity: 1 }
          ]
        };
      };

      const applyGroupedSelection = () => {
        const selection = getSelectionState();
        if (!selection?.variant) {
          if (variantInput) variantInput.value = '';
          if (variantDisplayEl) variantDisplayEl.textContent = 'Indisponível';
          if (unitPriceEl) {
            unitPriceEl.hidden = true;
            unitPriceEl.textContent = '';
          }
          if (helperEl) {
            helperEl.hidden = true;
            helperEl.textContent = '';
          }
          if (addBtn) {
            addBtn.disabled = true;
            addBtn.textContent = 'Indisponível';
            addBtn._originalText = addBtn.textContent;
          }
          if (statusEl) {
            statusEl.textContent = 'Esta combinação não está disponível.';
            statusEl.className = 'product-status is-error';
          }
          if (variantMinusBtn) variantMinusBtn.disabled = true;
          if (variantPlusBtn) variantPlusBtn.disabled = true;
          return;
        }

        const { label, variant, price, compareAtPrice, unitPriceText, helperText } = selection;
        updateProductUI({
          label,
          variant,
          price,
          compareAtPrice,
          unitPriceText,
          helperText: currentVariantGroups.length > 1 ? helperText : ''
        });

        if (variantMinusBtn) variantMinusBtn.disabled = selectedIndex <= 0 && extraStepCount <= 0;
        if (variantPlusBtn) variantPlusBtn.disabled = !quantityExpansion && selectedIndex >= currentVariantGroups.length - 1;
      };

      secondaryOptionInputs.forEach((input) => {
        input.addEventListener('change', () => {
          const currentLabel = getSelectionState()?.label || currentVariantGroups[selectedIndex]?.label || '';
          syncAvailableGroups(currentLabel);
          applyGroupedSelection();
        });
      });

      variantMinusBtn?.addEventListener('click', () => {
        if (quantityExpansion && extraStepCount > 0) {
          extraStepCount -= 1;
          applyGroupedSelection();
          return;
        }
        if (selectedIndex <= 0) return;
        selectedIndex -= 1;
        extraStepCount = 0;
        applyGroupedSelection();
      });

      variantPlusBtn?.addEventListener('click', () => {
        if (quantityExpansion && selectedIndex >= currentVariantGroups.length - 1) {
          extraStepCount += 1;
          applyGroupedSelection();
          return;
        }
        if (selectedIndex >= currentVariantGroups.length - 1) return;
        selectedIndex += 1;
        extraStepCount = 0;
        applyGroupedSelection();
      });

      syncAvailableGroups(primaryOptionInputs.find((input) => input.checked)?.value || '');
      applyGroupedSelection();

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const selection = getSelectionState();
        const cartItems = selection?.cartItems || [];
        if (!selection?.variant?.available || !cartItems.length) return;

        if (addBtn) setButtonLoading(addBtn, true);
        if (statusEl) {
          statusEl.textContent = '';
          statusEl.className = 'product-status';
        }

        try {
          const cart = await addToCart(cartItems.length === 1 ? cartItems[0] : cartItems);
          updateCartState(cart);
          if (addBtn) setButtonSuccess(addBtn);
          if (statusEl) {
            statusEl.textContent = 'Adicionado ao carrinho!';
            statusEl.className = 'product-status is-success';
            setTimeout(() => {
              if (statusEl) {
                statusEl.textContent = '';
                statusEl.className = 'product-status';
              }
            }, 3000);
          }
          openCartDrawer();
        } catch (err) {
          if (addBtn) setButtonLoading(addBtn, false);
          if (statusEl) {
            statusEl.textContent = err.message || 'Não foi possível adicionar. Tente novamente.';
            statusEl.className = 'product-status is-error';
          }
        }
      });

      return;
    }

    const findVariant = () => {
      if (!optionInputs.length) return variants[0];
      const selected = getSelectedOptionsByPosition();
      return variants.find((variant) => variant.options.every((optionValue, index) => optionValue === selected[index])) || null;
    };

    const applyVariant = (variant) => {
      if (!variant) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = 'Indisponível';
          addBtn._originalText = addBtn.textContent;
        }
        if (unitPriceEl) {
          unitPriceEl.hidden = true;
          unitPriceEl.textContent = '';
        }
        if (helperEl) {
          helperEl.hidden = true;
          helperEl.textContent = '';
        }
        return;
      }

      const label = getPrimaryVariantLabel(variant);
      updateProductUI({
        label,
        variant,
        price: variant.price,
        compareAtPrice: variant.compare_at_price,
        unitPriceText: getVariantUnitPriceText(variant, label),
        helperText: '',
        quantity: 1,
        resetQuantity: true
      });
    };

    const getSelectedQuantity = () => Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);

    const syncSimpleQuantityPricing = (variant, { resetQuantity = false } = {}) => {
      if (!variant) return;

      const label = getPrimaryVariantLabel(variant);
      const quantity = resetQuantity ? 1 : getSelectedQuantity();

      updateProductUI({
        label,
        variant,
        price: (Number(variant.price) || 0) * quantity,
        compareAtPrice: (Number(variant.compare_at_price) || 0) * quantity,
        unitPriceText: getVariantUnitPriceText(variant, label),
        helperText: '',
        quantity,
        resetQuantity
      });
    };

    optionInputs.forEach((input) => input.addEventListener('change', () => syncSimpleQuantityPricing(findVariant(), { resetQuantity: true })));
    qtyInput?.addEventListener('change', () => syncSimpleQuantityPricing(findVariant(), { resetQuantity: false }));
    syncSimpleQuantityPricing(findVariant(), { resetQuantity: true });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variant = findVariant();
      if (!variant?.available) return;

      const qty = parseInt(qtyInput?.value || '1', 10);

      if (addBtn) setButtonLoading(addBtn, true);
      if (statusEl) {
        statusEl.textContent = '';
        statusEl.className = 'product-status';
      }

      try {
        const cart = await addToCart({ id: variant.id, quantity: qty });
        updateCartState(cart);
        if (addBtn) setButtonSuccess(addBtn);
        if (statusEl) {
          statusEl.textContent = 'Adicionado ao carrinho!';
          statusEl.className = 'product-status is-success';
          setTimeout(() => {
            if (statusEl) {
              statusEl.textContent = '';
              statusEl.className = 'product-status';
            }
          }, 3000);
        }
        openCartDrawer();
      } catch (err) {
        if (addBtn) setButtonLoading(addBtn, false);
        if (statusEl) {
          statusEl.textContent = err.message || 'Não foi possível adicionar. Tente novamente.';
          statusEl.className = 'product-status is-error';
        }
      }
    });
  });
}

function initProductCardForms(root) {
  root.querySelectorAll('[data-card-form]').forEach((form) => {
    if (form.dataset.ready === 'true') return;
    form.dataset.ready = 'true';

    const cardEl = form.closest('.product-card');
    const variantsEl = form.querySelector('[data-card-variants]');
    let variants = [];
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent); } catch {}
      variantsEl.remove();
    }

    const variantInput = form.querySelector('[data-card-variant-id]');
    const qtyInput     = form.querySelector('[data-qty-input]');
    const displayEl    = form.querySelector('[data-card-variant-label]');
    const addBtn       = form.querySelector('[data-card-add]');
    const statusEl     = form.querySelector('[data-card-status]');
    const badgeEl      = cardEl?.querySelector('[data-card-variant-badge]');
    const helperEl     = form.querySelector('[data-card-helper]');
    const priceMetaEl  = cardEl?.querySelector('[data-card-price-meta]');
    const priceEl      = cardEl?.querySelector('[data-card-price-current]');
    const compareEl    = cardEl?.querySelector('[data-card-price-compare]');
    const unitPriceEl  = cardEl?.querySelector('[data-card-unit-price]');
    const btnMinus     = form.querySelector('[data-qty-minus]');
    const btnPlus      = form.querySelector('[data-qty-plus]');

    const variantGroups = buildVariantGroups(variants);
    if (!variantGroups.length) return;
    const quantityExpansion = buildQuantityExpansionConfig(variantGroups);
    const simpleQuantityMode = variantGroups.length === 1;

    let selectedIndex = variantGroups.findIndex((group) => group.variant?.available);
    if (selectedIndex < 0) selectedIndex = 0;
    let extraStepCount = 0;
    let simpleQuantity = Math.max(1, parseInt(qtyInput?.value || '1', 10));

    const getSimpleQuantityLabel = () => {
      const baseLabel = variantGroups[selectedIndex]?.label || '';
      if (simpleQuantity === 1) return baseLabel || '1 un';
      const measurement = variantGroups[selectedIndex]?.measurement;
      if (measurement) {
        const baseAmount = getMeasurementBaseAmount(measurement);
        if (baseAmount > 0) {
          const total = baseAmount * simpleQuantity;
          if (measurement.kind === 'weight') return formatWeightLabelFromGrams(total);
          if (measurement.kind === 'volume') return formatVolumeLabel(total);
          if (measurement.kind === 'unit') return `${total} un`;
        }
      }
      return `${simpleQuantity} × ${baseLabel || 'un'}`;
    };

    const getSimpleQuantityHelperText = () => {
      const measurement = variantGroups[selectedIndex]?.measurement;
      if (!measurement) return 'Use + e - para ajustar a quantidade.';
      if (measurement.kind === 'weight') return 'Use + e - para ajustar o peso.';
      if (measurement.kind === 'volume') return 'Use + e - para ajustar o volume.';
      return 'Use + e - para ajustar a quantidade.';
    };

    const getSelectionState = () => {
      const activeGroup = variantGroups[selectedIndex] || variantGroups[0];
      if (!activeGroup?.variant) return null;

      if (simpleQuantityMode) {
        const totalPrice = (Number(activeGroup.variant.price) || 0) * simpleQuantity;
        const totalCompareAtPrice = (Number(activeGroup.variant.compare_at_price) || 0) * simpleQuantity;

        return {
          label: getSimpleQuantityLabel(),
          variant: activeGroup.variant,
          price: totalPrice,
          compareAtPrice: totalCompareAtPrice,
          unitPriceText: getVariantUnitPriceText(activeGroup.variant, activeGroup.label),
          helperText: getSimpleQuantityHelperText(),
          cartItems: [
            { id: activeGroup.variant.id, quantity: simpleQuantity }
          ]
        };
      }

      if (quantityExpansion && selectedIndex === variantGroups.length - 1 && extraStepCount > 0) {
        const expandedSelection = buildExpandedSelectionState(
          quantityExpansion,
          extraStepCount,
          getVariantHelperText(variantGroups)
        );
        if (expandedSelection) return expandedSelection;
      }

      return {
        label: activeGroup.label,
        variant: activeGroup.variant,
        price: activeGroup.variant.price,
        compareAtPrice: activeGroup.variant.compare_at_price,
        unitPriceText: getVariantUnitPriceText(activeGroup.variant, activeGroup.label),
        helperText: getVariantHelperText(variantGroups),
        cartItems: [
          { id: activeGroup.variant.id, quantity: 1 }
        ]
      };
    };

    const applyVariantGroup = () => {
      const selection = getSelectionState();
      if (!selection?.variant) return;

      const { label, variant, price, compareAtPrice, unitPriceText, helperText } = selection;

      if (variantInput) variantInput.value = variant.id;
      if (qtyInput) qtyInput.value = simpleQuantityMode ? String(simpleQuantity) : '1';
      if (displayEl) displayEl.textContent = label;
      if (badgeEl) badgeEl.textContent = label;

      if (priceEl) priceEl.textContent = formatMoney(price);
      if (priceMetaEl) priceMetaEl.setAttribute('content', String((price || 0) / 100));

      if (compareEl) {
        const onSale = Number(compareAtPrice) > Number(price);
        compareEl.hidden = !onSale;
        compareEl.textContent = onSale ? formatMoney(compareAtPrice) : '';
      }

      if (unitPriceEl) {
        unitPriceEl.hidden = !unitPriceText;
        unitPriceEl.textContent = unitPriceText;
      }

      if (helperEl) {
        helperEl.hidden = !helperText || (!simpleQuantityMode && variantGroups.length <= 1);
        helperEl.textContent = helperText;
      }

      if (btnMinus) {
        btnMinus.disabled = simpleQuantityMode
          ? simpleQuantity <= 1
          : (selectedIndex <= 0 && extraStepCount <= 0);
        btnMinus.setAttribute('aria-label', simpleQuantityMode ? 'Diminuir quantidade' : 'Selecionar medida anterior');
      }

      if (btnPlus) {
        btnPlus.disabled = simpleQuantityMode
          ? false
          : (!quantityExpansion && selectedIndex >= variantGroups.length - 1);
        btnPlus.setAttribute('aria-label', simpleQuantityMode ? 'Aumentar quantidade' : 'Selecionar próxima medida');
      }

      if (addBtn) {
        addBtn.classList.remove('is-loading', 'is-success');
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available
          ? (addBtn.dataset.availableLabel || 'Comprar')
          : (addBtn.dataset.unavailableLabel || 'Indisponível');
        addBtn.setAttribute(
          'aria-label',
          variant.available
            ? `Comprar ${label} por ${formatMoney(price)}`
            : 'Produto indisponivel'
        );
        addBtn._originalText = addBtn.textContent;
      }

      if (statusEl) {
        statusEl.textContent = '';
        statusEl.className = 'product-card__status';
      }
    };

    btnMinus?.addEventListener('click', () => {
      if (simpleQuantityMode) {
        if (simpleQuantity <= 1) return;
        simpleQuantity -= 1;
        applyVariantGroup();
        return;
      }

      if (quantityExpansion && extraStepCount > 0) {
        extraStepCount -= 1;
        applyVariantGroup();
        return;
      }
      if (selectedIndex <= 0) return;
      selectedIndex -= 1;
      extraStepCount = 0;
      applyVariantGroup();
    });

    btnPlus?.addEventListener('click', () => {
      if (simpleQuantityMode) {
        simpleQuantity += 1;
        applyVariantGroup();
        return;
      }

      if (quantityExpansion && selectedIndex >= variantGroups.length - 1) {
        extraStepCount += 1;
        applyVariantGroup();
        return;
      }
      if (selectedIndex >= variantGroups.length - 1) return;
      selectedIndex += 1;
      extraStepCount = 0;
      applyVariantGroup();
    });

    applyVariantGroup();

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const selection = getSelectionState();
      const cartItems = selection?.cartItems || [];
      if (!cartItems.length) return;

      setButtonLoading(addBtn, true);
      if (statusEl) { statusEl.textContent = ''; statusEl.className = 'product-card__status'; }

      try {
        const cart = await addToCart(cartItems.length === 1 ? cartItems[0] : cartItems);
        updateCartState(cart);
        setButtonSuccess(addBtn);
        openCartDrawer();
        if (statusEl) {
          statusEl.textContent = 'Produto adicionado ao carrinho.';
          statusEl.className = 'product-card__status is-success';
          setTimeout(() => { if (statusEl) { statusEl.textContent = ''; statusEl.className = 'product-card__status'; } }, 2500);
        }
      } catch (err) {
        setButtonLoading(addBtn, false);
        if (statusEl) {
          statusEl.textContent = err.message || 'Tente novamente.';
          statusEl.className = 'product-card__status is-error';
        }
      }
    });
  });
}

function initDeferredProductCardForms(root) {
  const forms = [...root.querySelectorAll('[data-card-form]')].filter((form) => form.dataset.deferReady !== 'true');
  if (!forms.length) return;

  if (!('IntersectionObserver' in window)) {
    initProductCardForms(root);
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const target = entry.target;
      initProductCardForms(target);
      observer.unobserve(target);
    });
  }, {
    rootMargin: '320px 0px'
  });

  forms.forEach((form) => {
    form.dataset.deferReady = 'true';
    const target = form.closest('.product-card') || form;

    const activate = () => {
      initProductCardForms(target);
      observer.unobserve(target);
    };

    target.addEventListener('pointerenter', activate, { once: true, passive: true });
    target.addEventListener('focusin', activate, { once: true });
    target.addEventListener('touchstart', activate, { once: true, passive: true });
    observer.observe(target);
  });
}

let cartState = null;

function initCartDrawer() {
  const drawer   = document.querySelector('[data-cart-drawer]');
  const body     = drawer?.querySelector('[data-cart-body]');
  if (!drawer || !body) return;

  const initialCart = parseJson(document.querySelector('[data-cart-state]')) || makeEmptyCart();
  cartState = buildCartState(drawer, body, initialCart);

  cartState.closeButtons.forEach(btn => btn.addEventListener('click', closeCartDrawer));
  drawer.addEventListener('click', onDrawerBodyClick);
  drawer.addEventListener('change', onDrawerChange);

  document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
    if (trigger.dataset.cartReady === 'true') return;
    trigger.dataset.cartReady = 'true';
    trigger.addEventListener('click', (e) => { e.preventDefault(); openCartDrawer(); });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartState?.drawer.classList.contains('is-open')) closeCartDrawer();
  });

  document.querySelector('[data-overlay]')?.addEventListener('click', () => {
    if (cartState?.drawer.classList.contains('is-open')) closeCartDrawer();
  });

  renderCartDrawer(initialCart);

  window.raminhoTheme = window.raminhoTheme || {};
  window.raminhoTheme.openCartDrawer = openCartDrawer;
}

function buildCartState(drawer, body, cart) {
  return {
    drawer,
    body,
    closeButtons: [...drawer.querySelectorAll('[data-cart-close]')],
    countEls: [...document.querySelectorAll('[data-cart-count]')],
    drawerCountEl: drawer.querySelector('[data-cart-drawer-count]'),
    subtotalEl: drawer.querySelector('[data-cart-subtotal]'),
    progressFill: drawer.querySelector('[data-progress-fill]'),
    progressText: drawer.querySelector('[data-progress-text]'),
    minimumEl: drawer.querySelector('[data-cart-minimum]'),
    footerEl: drawer.querySelector('[data-cart-footer]'),
    checkoutBtn: drawer.querySelector('[data-checkout-button]'),
    checkoutNoteEl: drawer.querySelector('[data-cart-checkout-note]'),
    cart
  };
}

function openCartDrawer() {
  if (!cartState) return;
  cartState.drawer.classList.add('is-open');
  document.querySelector('[data-overlay]')?.classList.add('is-active');
  document.body.style.overflow = 'hidden';
  cartState.drawer.setAttribute('aria-hidden', 'false');
}
function closeCartDrawer() {
  if (!cartState) return;
  cartState.drawer.classList.remove('is-open');
  document.querySelector('[data-overlay]')?.classList.remove('is-active');
  // Only restore scroll if mobile nav is also closed
  if (!document.querySelector('[data-mobile-nav]')?.classList.contains('is-open')) {
    document.body.style.overflow = '';
  }
  cartState.drawer.setAttribute('aria-hidden', 'true');
}

function openCartFromUrl() {
  if (new URLSearchParams(window.location.search).get('cart') === 'open') openCartDrawer();
}

function startCheckoutFlow() {
  closeCartDrawer();
  if (typeof window.yampiClick === 'function') {
    window.yampiClick();
    return;
  }
  const fallbackUrl = window.raminhoTheme?.routes?.checkout || '/checkout';
  window.location.href = fallbackUrl;
}

function initCartPageCheckout() {
  const summary = document.querySelector('[data-cart-page-summary]');
  const checkoutArea = summary?.querySelector('[data-cart-page-checkout]');
  if (!summary || !checkoutArea) return;

  const minimum = parseInt(
    summary.getAttribute('data-minimum-cents')
      || window.raminhoTheme?.cartRules?.minimumCents
      || '0',
    10
  );
  const total = parseInt(summary.getAttribute('data-total-price') || '0', 10);
  const itemCount = parseInt(summary.getAttribute('data-item-count') || '0', 10);
  const checkoutUnlocked = itemCount > 0 && total >= minimum;

  const syncButtonState = (button) => {
    if (!(button instanceof HTMLElement)) return;

    button.classList.toggle('is-disabled', !checkoutUnlocked);
    button.setAttribute('aria-disabled', checkoutUnlocked ? 'false' : 'true');

    if (button.tagName === 'BUTTON') {
      button.disabled = !checkoutUnlocked;
    }

    if (!checkoutUnlocked) {
      button.setAttribute('tabindex', '-1');
    } else {
      button.removeAttribute('tabindex');
    }
  };

  const syncCheckoutButtons = () => {
    checkoutArea
      .querySelectorAll('[data-cart-page-checkout-anchor], .ymp_check_mainBtn, .ymp_elem_mainBtn')
      .forEach(syncButtonState);
  };

  const routeThroughYampi = () => {
    if (typeof window.yampiClick !== 'function') return false;
    window.yampiClick();
    return true;
  };

  checkoutArea.addEventListener('click', (event) => {
    const target = event.target instanceof Element
      ? event.target.closest('[data-cart-page-checkout-anchor], .ymp_check_mainBtn, .ymp_elem_mainBtn')
      : null;

    if (!target) return;

    if (!checkoutUnlocked) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (target.matches('[data-cart-page-checkout-anchor]') && routeThroughYampi()) {
      event.preventDefault();
    }
  }, true);

  checkoutArea.addEventListener('submit', (event) => {
    if (checkoutUnlocked) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  const observer = new MutationObserver(syncCheckoutButtons);
  observer.observe(checkoutArea, { childList: true, subtree: true });

  syncCheckoutButtons();
  window.setTimeout(syncCheckoutButtons, 800);
  window.setTimeout(syncCheckoutButtons, 1800);
}

async function onDrawerBodyClick(e) {
  const checkoutButton = e.target.closest('[data-checkout-button]');
  if (checkoutButton) {
    e.preventDefault();

    if (checkoutButton.classList.contains('is-disabled')) {
      return;
    }

    startCheckoutFlow();
    return;
  }

  const btn = e.target.closest('[data-cart-remove]');
  if (btn) {
    const key = btn.getAttribute('data-cart-remove');
    try {
      const cart = await changeCartItem(key, 0);
      updateCartState(cart);
    } catch {}
  }
}

async function onDrawerChange(e) {
  const input = e.target.closest('[data-cart-qty-input]');
  if (input) {
    const key = input.getAttribute('data-item-key');
    const qty = parseInt(input.value, 10);
    try {
      const cart = await changeCartItem(key, qty);
      updateCartState(cart);
    } catch {}
  }
}

function updateCartState(cart) {
  if (!cartState) return;
  cartState.cart = cart;
  renderCartDrawer(cart);
  updateCountBadges(cart.item_count);
}

function renderCartDrawer(cart) {
  if (!cartState) return;

  const itemCount = cart.item_count || 0;
  const total = cart.total_price || 0;
  const threshold = window.raminhoTheme?.cartRules?.freeShipCents || 59900;
  const minimum = window.raminhoTheme?.cartRules?.minimumCents || 12000;
  const remainingForShipping = Math.max(0, threshold - total);
  const remainingForMinimum = Math.max(0, minimum - total);
  const hasItems = itemCount > 0;
  const checkoutUnlocked = hasItems && total >= minimum;
  const pct = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 0;

  updateCountBadges(itemCount);

  if (cartState.drawerCountEl) {
    cartState.drawerCountEl.textContent = formatCartItemCount(itemCount);
  }

  if (cartState.subtotalEl) {
    cartState.subtotalEl.textContent = formatMoney(total);
  }

  if (cartState.progressFill) {
    cartState.progressFill.style.width = `${pct}%`;
  }

  if (cartState.progressText) {
    cartState.progressText.innerHTML = hasItems && remainingForShipping === 0
      ? '<span>Frete grátis liberado. Entrega exclusiva na sua porta.</span>'
      : hasItems
        ? `Faltam <span>${formatMoney(remainingForShipping)}</span> para frete grátis`
        : `Frete grátis acima de ${formatMoney(threshold)}`;
  }

  if (cartState.minimumEl) {
    const showMinimumWarning = hasItems && total < minimum;
    cartState.minimumEl.classList.toggle('is-visible', showMinimumWarning);
    cartState.minimumEl.textContent = showMinimumWarning
      ? `Pedido mínimo de ${formatMoney(minimum)}. Adicione mais ${formatMoney(remainingForMinimum)} para liberar o checkout.`
      : '';
  }

  if (cartState.checkoutNoteEl) {
    const showCheckoutNote = hasItems && total < minimum;
    cartState.checkoutNoteEl.hidden = !showCheckoutNote;
    cartState.checkoutNoteEl.textContent = showCheckoutNote
      ? `Checkout liberado a partir de ${formatMoney(minimum)}.`
      : '';
  }

  if (cartState.footerEl) {
    cartState.footerEl.hidden = !hasItems;
  }

  if (cartState.checkoutBtn) {
    cartState.checkoutBtn.classList.toggle('is-disabled', !checkoutUnlocked);
    cartState.checkoutBtn.setAttribute('aria-disabled', checkoutUnlocked ? 'false' : 'true');
    cartState.checkoutBtn.disabled = !checkoutUnlocked;
    cartState.checkoutBtn.toggleAttribute('disabled', !checkoutUnlocked);
    if (checkoutUnlocked) {
      cartState.checkoutBtn.removeAttribute('tabindex');
    } else {
      cartState.checkoutBtn.setAttribute('tabindex', '-1');
    }
  }

  if (!cart.items || cart.items.length === 0) {
    cartState.body.innerHTML = emptyCartHTML();
    return;
  }

  cartState.body.innerHTML = cart.items.map((item) => cartItemHTML(item)).join('');

  initQuantityPickers(cartState.body);

  cartState.body.querySelectorAll('[data-cart-qty-input]').forEach((inp) => {
    inp.addEventListener('change', async () => {
      const key = inp.getAttribute('data-item-key');
      const qty = parseInt(inp.value, 10);
      try {
        const updatedCart = await changeCartItem(key, qty);
        updateCartState(updatedCart);
      } catch {}
    });
  });
}
function updateCountBadges(count) {
  document.querySelectorAll('[data-cart-count]').forEach(el => {
    el.textContent = count > 0 ? count : '';
  });
}

function cartItemHTML(item) {
  const img = item.featured_image?.url
    ? `<img src="${item.featured_image.url}&width=144" alt="${escapeHtml(item.title)}" loading="lazy" width="72" height="72">`
    : `<div style="width:72px;height:72px;background:var(--color-bg);border-radius:var(--radius-s);"></div>`;

  const variantLine = (item.variant_title && item.variant_title !== 'Default Title')
    ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>`
    : '';

  return `
    <div class="cart-item" data-item-key="${item.key}">
      <div class="cart-item__image">${img}</div>
      <div>
        <p class="cart-item__title">${escapeHtml(item.title)}</p>
        ${variantLine}
        <div class="cart-item__controls">
          <div class="cart-item__qty">
            <button type="button" aria-label="Diminuir" onclick="(function(btn){
              const inp = btn.closest('.cart-item__qty').querySelector('[data-cart-qty-input]');
              if(inp){ inp.value = Math.max(1, parseInt(inp.value,10)-1); inp.dispatchEvent(new Event('change',{bubbles:true})); }
            })(this)">-</button>
            <span>${item.quantity}</span>
            <button type="button" aria-label="Aumentar" onclick="(function(btn){
              const inp = btn.closest('.cart-item__qty').querySelector('[data-cart-qty-input]');
              if(inp){ inp.value = parseInt(inp.value,10)+1; inp.dispatchEvent(new Event('change',{bubbles:true})); }
            })(this)">+</button>
            <input type="hidden" data-cart-qty-input data-item-key="${item.key}" value="${item.quantity}">
          </div>
          <span class="cart-item__price">${formatMoney(item.line_price)}</span>
        </div>
        <button class="cart-item__remove" data-cart-remove="${item.key}" type="button">Remover</button>
      </div>
    </div>`;
}
function emptyCartHTML() {
  return `
    <div class="cart-empty-state">
      <div class="cart-empty-state__icon">&#128722;</div>
      <h3>Seu carrinho ainda está vazio</h3>
      <p>Adicione produtos para continuar sua compra.</p>
      <a class="btn btn--primary" href="/collections/all" onclick="closeCartDrawer()">Explorar produtos</a>
    </div>`;
}
function initSortSelect() {
  const sel = document.querySelector('[data-sort-select]');
  if (!sel) return;
  sel.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    window.location.href = url.toString();
  });
}

async function addToCart(items) {
  const payload = Array.isArray(items) ? items : [items];
  const resp = await fetch(window.raminhoTheme.routes.cartAdd, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ items: payload })
  });
  if (!resp.ok) {
    let errMsg = 'Não foi possível adicionar o produto.';
    try {
      const data = await resp.json();
      if (data.description) errMsg = data.description;
    } catch {}
    throw new Error(errMsg);
  }
  return fetchCart();
}

async function changeCartItem(key, quantity) {
  const resp = await fetch(window.raminhoTheme.routes.cartChange, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: key, quantity })
  });
  if (!resp.ok) throw new Error('Erro ao atualizar carrinho.');
  return resp.json();
}

async function fetchCart() {
  const resp = await fetch(window.raminhoTheme.routes.cartJson, {
    headers: { 'Accept': 'application/json' }
  });
  if (!resp.ok) throw new Error('Erro ao buscar carrinho.');
  return resp.json();
}

function makeEmptyCart() {
  return { item_count: 0, items: [], total_price: 0, items_subtotal_price: 0 };
}

function setButtonLoading(btn, loading) {
  if (!btn) return;
  if (loading) {
    btn._originalText = btn.textContent;
    btn.classList.add('is-loading');
    btn.disabled = true;
  } else {
    btn.classList.remove('is-loading');
    btn.disabled = false;
    if (btn._originalText) btn.textContent = btn._originalText;
  }
}

function setButtonSuccess(btn) {
  if (!btn) return;
  btn.classList.remove('is-loading');
  btn.classList.add('is-success');
  btn.textContent = btn.dataset.successLabel || 'Adicionado ao carrinho';
  btn.disabled = false;
  setTimeout(() => {
    btn.classList.remove('is-success');
    if (btn._originalText) btn.textContent = btn._originalText;
  }, 2500);
}

function formatCartItemCount(count) {
  return `${count} ${count === 1 ? 'item' : 'itens'}`;
}
function formatMoney(cents) {
  const format = window.raminhoTheme?.moneyFormat || 'R$ {{amount}}';
  if (!cents && cents !== 0) return '';
  const amount = (cents / 100).toFixed(2).replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, '$1.');
  return format.replace('{{amount}}', amount)
               .replace('{{amount_no_decimals}}', Math.round(cents / 100))
               .replace('{{amount_with_comma_separator}}', amount);
}

function buildVariantGroups(variants) {
  if (!Array.isArray(variants) || !variants.length) return [];

  const seen = new Set();
  const groups = [];

  variants.forEach((variant, index) => {
    const label = getPrimaryVariantLabel(variant);
    const key = normalizeLabel(label);
    if (seen.has(key)) return;

    const preferredVariant = variants.find((candidate) => normalizeLabel(getPrimaryVariantLabel(candidate)) === key && candidate.available)
      || variants.find((candidate) => normalizeLabel(getPrimaryVariantLabel(candidate)) === key)
      || variant;

    seen.add(key);
    groups.push({
      key,
      label,
      variant: preferredVariant,
      measurement: parseVariantMeasurement(label),
      originalIndex: index
    });
  });

  return groups.sort(compareVariantGroups);
}

function getPrimaryVariantLabel(variant) {
  const rawLabel = variant?.option1 || variant?.public_title || variant?.title || '';
  const label = String(rawLabel).split('/')[0].trim();
  if (!label || /^default title$/i.test(label)) return '1 un';
  return label;
}

function getVariantHelperText(variantGroups) {
  if (!Array.isArray(variantGroups) || variantGroups.length <= 1) return '';

  const label = variantGroups[0]?.label || '';
  const measurement = parseVariantMeasurement(label);
  if (!measurement) return 'Use + e - para ajustar a seleção.';
  if (measurement.kind === 'weight') return 'Use + e - para ajustar o peso.';
  if (measurement.kind === 'volume') return 'Use + e - para ajustar o volume.';
  if (measurement.kind === 'unit') return 'Use + e - para ajustar a quantidade.';
  return 'Use + e - para ajustar a seleção.';
}

function getVariantUnitPriceText(variant, label) {
  if (variant?.unit_price && variant?.unit_price_measurement?.reference_unit) {
    return `${formatMoney(variant.unit_price)} / ${String(variant.unit_price_measurement.reference_unit).toLowerCase()}`;
  }

  const measurement = parseVariantMeasurement(label);
  if (!measurement || measurement.kind !== 'weight') return '';

  const grams = getWeightInGrams(measurement);
  return getUnitPriceTextFromTotalWeight(variant.price, grams);
}

function buildQuantityExpansionConfig(variantGroups) {
  if (!Array.isArray(variantGroups) || variantGroups.length < 2) return null;

  const measuredGroups = variantGroups
    .filter((group) => group?.variant?.available)
    .map((group) => ({
      ...group,
      baseAmount: getMeasurementBaseAmount(group.measurement)
    }))
    .filter((group) => group.measurement && Number.isFinite(group.baseAmount) && group.baseAmount > 0);

  if (measuredGroups.length < 2) return null;

  const maxGroup = measuredGroups[measuredGroups.length - 1];
  const compatibleGroups = measuredGroups.filter((group) => group.measurement.kind === maxGroup.measurement.kind);
  if (compatibleGroups.length < 2) return null;

  const stepGroup = compatibleGroups[compatibleGroups.length - 2];
  const maxBaseAmount = maxGroup.baseAmount;
  const stepBaseAmount = stepGroup.baseAmount;

  if (!Number.isFinite(maxBaseAmount) || !Number.isFinite(stepBaseAmount) || stepBaseAmount <= 0) return null;

  return {
    maxGroup,
    stepGroup,
    kind: maxGroup.measurement.kind,
    maxBaseAmount,
    stepBaseAmount,
    expandableGroups: compatibleGroups,
    compositionCache: new Map()
  };
}

function buildExpandedSelectionState(expansionConfig, extraStepCount, helperText = '') {
  if (!expansionConfig || !Number.isFinite(extraStepCount) || extraStepCount <= 0) return null;

  const totalBaseAmount = expansionConfig.maxBaseAmount + (extraStepCount * expansionConfig.stepBaseAmount);
  const cartItems = buildExpandedCartItems(expansionConfig, totalBaseAmount);
  if (!cartItems.length) return null;

  const composition = summarizeExpandedCartItems(expansionConfig, cartItems);
  const leadGroup = composition.leadGroup || expansionConfig.maxGroup;

  return {
    label: formatExpandedMeasurementLabel(expansionConfig, totalBaseAmount),
    variant: leadGroup?.variant || expansionConfig.maxGroup.variant,
    price: composition.totalPrice,
    compareAtPrice: composition.totalCompareAtPrice,
    unitPriceText: expansionConfig.kind === 'weight'
      ? getUnitPriceTextFromTotalWeight(composition.totalPrice, totalBaseAmount)
      : '',
    helperText,
    cartItems
  };
}

function buildExpandedCartItems(expansionConfig, totalBaseAmount) {
  if (!expansionConfig || !Number.isFinite(totalBaseAmount) || totalBaseAmount <= 0) return [];

  const cached = expansionConfig.compositionCache?.get(totalBaseAmount);
  if (cached) {
    return cached.map((item) => ({ ...item }));
  }

  const groups = [...(expansionConfig.expandableGroups || [])]
    .filter((group) => group?.variant?.id && Number.isFinite(group.baseAmount) && group.baseAmount > 0)
    .sort((a, b) => b.baseAmount - a.baseAmount);

  if (!groups.length) return [];

  const bestByAmount = new Array(totalBaseAmount + 1).fill(null);
  bestByAmount[0] = new Array(groups.length).fill(0);

  for (let amount = 1; amount <= totalBaseAmount; amount += 1) {
    let bestCounts = null;

    groups.forEach((group, index) => {
      const previousAmount = amount - group.baseAmount;
      if (previousAmount < 0) return;

      const previousCounts = bestByAmount[previousAmount];
      if (!previousCounts) return;

      const candidateCounts = previousCounts.slice();
      candidateCounts[index] += 1;

      if (isPreferredCartComposition(candidateCounts, bestCounts)) {
        bestCounts = candidateCounts;
      }
    });

    bestByAmount[amount] = bestCounts;
  }

  const counts = bestByAmount[totalBaseAmount];
  if (!counts) return [];

  const cartItems = counts
    .map((quantity, index) => (
      quantity > 0
        ? { id: groups[index].variant.id, quantity }
        : null
    ))
    .filter(Boolean);

  expansionConfig.compositionCache?.set(
    totalBaseAmount,
    cartItems.map((item) => ({ ...item }))
  );

  return cartItems;
}

function isPreferredCartComposition(candidateCounts, currentCounts) {
  if (!candidateCounts) return false;
  if (!currentCounts) return true;

  const candidateTotal = sumCartComposition(candidateCounts);
  const currentTotal = sumCartComposition(currentCounts);
  if (candidateTotal !== currentTotal) return candidateTotal < currentTotal;

  const candidateLines = countCartCompositionLines(candidateCounts);
  const currentLines = countCartCompositionLines(currentCounts);
  if (candidateLines !== currentLines) return candidateLines < currentLines;

  for (let index = 0; index < candidateCounts.length; index += 1) {
    if (candidateCounts[index] === currentCounts[index]) continue;
    return candidateCounts[index] > currentCounts[index];
  }

  return false;
}

function sumCartComposition(counts) {
  return counts.reduce((total, count) => total + count, 0);
}

function countCartCompositionLines(counts) {
  return counts.reduce((total, count) => total + (count > 0 ? 1 : 0), 0);
}

function summarizeExpandedCartItems(expansionConfig, cartItems) {
  const groupsByVariantId = new Map(
    (expansionConfig.expandableGroups || []).map((group) => [String(group.variant.id), group])
  );

  return cartItems.reduce((summary, item) => {
    const group = groupsByVariantId.get(String(item.id));
    if (!group) return summary;

    summary.totalPrice += (Number(group.variant.price) || 0) * item.quantity;
    summary.totalCompareAtPrice += (Number(group.variant.compare_at_price) || 0) * item.quantity;

    if (!summary.leadGroup || group.baseAmount > summary.leadGroup.baseAmount) {
      summary.leadGroup = group;
    }

    return summary;
  }, {
    totalPrice: 0,
    totalCompareAtPrice: 0,
    leadGroup: null
  });
}

function compareVariantGroups(a, b) {
  const measurementA = a?.measurement;
  const measurementB = b?.measurement;

  if (measurementA && measurementB && measurementA.kind === measurementB.kind) {
    const amountA = getMeasurementBaseAmount(measurementA);
    const amountB = getMeasurementBaseAmount(measurementB);

    if (amountA !== amountB) return amountA - amountB;
  }

  return (a?.originalIndex || 0) - (b?.originalIndex || 0);
}

function getMeasurementBaseAmount(measurement) {
  if (!measurement) return 0;
  if (measurement.kind === 'weight') return getWeightInGrams(measurement);
  if (measurement.kind === 'volume') {
    if (measurement.unit === 'l') return Math.round(measurement.amount * 1000);
    if (measurement.unit === 'ml') return Math.round(measurement.amount);
  }
  if (measurement.kind === 'unit') return Math.round(measurement.amount);
  return 0;
}

function formatExpandedMeasurementLabel(expansionConfig, totalBaseAmount) {
  if (!expansionConfig || !Number.isFinite(totalBaseAmount) || totalBaseAmount <= 0) return '';
  if (expansionConfig.kind === 'weight') return formatWeightLabelFromGrams(totalBaseAmount);
  if (expansionConfig.kind === 'unit') return `${Math.round(totalBaseAmount)} un`;
  if (expansionConfig.kind === 'volume') return formatVolumeLabel(totalBaseAmount);
  return expansionConfig.maxGroup?.label || '';
}

function getWeightInGrams(measurement) {
  if (!measurement || measurement.kind !== 'weight') return 0;
  if (measurement.unit === 'kg') return Math.round(measurement.amount * 1000);
  if (measurement.unit === 'g') return Math.round(measurement.amount);
  return 0;
}

function getUnitPriceTextFromTotalWeight(totalCents, grams) {
  if (!Number.isFinite(totalCents) || totalCents <= 0) return '';
  if (!Number.isFinite(grams) || grams <= 0) return '';

  const perKgCents = Math.round((totalCents * 1000) / grams);
  if (!Number.isFinite(perKgCents) || perKgCents <= 0) return '';
  return `${formatMoney(perKgCents)} / kg`;
}

function formatWeightLabelFromGrams(grams) {
  if (!Number.isFinite(grams) || grams <= 0) return '';

  if (grams >= 1000) {
    const kilos = grams / 1000;
    const formatted = Number.isInteger(kilos)
      ? String(kilos)
      : kilos.toFixed(2).replace(/0+$/, '').replace(/\.$/, '').replace('.', ',');
    return `${formatted} Kg`;
  }

  return `${Math.round(grams)} g`;
}

function formatVolumeLabel(totalMilliliters) {
  if (!Number.isFinite(totalMilliliters) || totalMilliliters <= 0) return '';

  if (totalMilliliters >= 1000) {
    const liters = totalMilliliters / 1000;
    const formatted = Number.isInteger(liters)
      ? String(liters)
      : liters.toFixed(2).replace(/0+$/, '').replace(/\.$/, '').replace('.', ',');
    return `${formatted} L`;
  }

  return `${Math.round(totalMilliliters)} ml`;
}

function parseVariantMeasurement(label) {
  if (!label) return null;

  const match = String(label).trim().toLowerCase().match(/(\d+(?:[.,]\d+)?)\s*(kg|g|un|und|unidade|ml|l)\b/);
  if (!match) return null;

  const amount = Number.parseFloat(match[1].replace(',', '.'));
  const unit = match[2];
  if (!Number.isFinite(amount) || amount <= 0) return null;

  if (unit === 'kg' || unit === 'g') return { kind: 'weight', amount, unit };
  if (unit === 'ml' || unit === 'l') return { kind: 'volume', amount, unit };
  return { kind: 'unit', amount, unit };
}

function normalizeLabel(value) {
  return String(value || '').trim().toLowerCase();
}
function parseJson(el) {
  if (!el) return null;
  try { return JSON.parse(el.textContent); } catch { return null; }
}

function escapeHtml(str) {
  if (typeof str !== 'string') return '';
  return str.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    parseVariantMeasurement,
    buildVariantGroups,
    getPrimaryVariantLabel,
    getVariantHelperText,
    getVariantUnitPriceText,
    buildQuantityExpansionConfig,
    buildExpandedCartItems,
    buildExpandedSelectionState,
    getMeasurementBaseAmount,
    getWeightInGrams,
    getUnitPriceTextFromTotalWeight,
    formatWeightLabelFromGrams,
    formatVolumeLabel,
    formatExpandedMeasurementLabel,
    compareVariantGroups,
    normalizeLabel,
    formatMoney,
    escapeHtml,
  };
}



